/**
 * CalendarHeatmapRenderer — daily/weekly seasonality via ECharts calendar
 * coordinate system. Replaces the legacy FyEndSurgeSection internals (Phase
 * B3) so the FY-end surge appears as the actual phenomenon, not a Q4-share
 * KPI.
 *
 * Expects rows with a date-bearing field and a numeric metric. The default
 * `dateField` is 'date'; for fy_end_surge data with `fiscal_year` +
 * `fiscal_month`, the caller can pass a pre-built `points` array.
 *
 * Phase C4 of issue #90 v3 plan.
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey } from './types'
import { METRIC_LABELS, formatMetric } from './types'
import { useExecChartTheme, makeChartReady } from './echarts-theme'

interface Props {
  data: PerfRow[]
  metrics: MetricKey[]
  /** Field on each row that holds the date in YYYY-MM-DD form. */
  dateField?: string
  /**
   * Pre-built [date, value] tuples — bypasses dateField extraction.
   * Use when the API returns FY+month rather than a date column.
   */
  points?: [string, number][]
  /** Calendar range: a year string ('2024') or [start, end] dates. */
  range?: string | [string, string]
  /** ECharts group key for cross-chart hover sync (Phase F1). */
  chartGroup?: string
}

export default function CalendarHeatmapRenderer({
  data, metrics, dateField = 'date', points, range, chartGroup,
}: Props) {
  const theme = useExecChartTheme()
  const metric = metrics[0]

  const { values, computedRange } = useMemo(() => {
    let pts: [string, number][]
    if (points) {
      pts = points
    } else {
      pts = []
      for (const row of data) {
        const d = row[dateField]
        if (!d) continue
        const v = row[metric]
        const num = typeof v === 'number' ? v : parseFloat(String(v))
        if (Number.isNaN(num)) continue
        pts.push([String(d), num])
      }
    }
    let r: string | [string, string]
    if (range) r = range
    else if (pts.length > 0) {
      const dates = pts.map((p) => p[0]).sort()
      r = [dates[0], dates[dates.length - 1]]
    } else {
      r = String(new Date().getFullYear())
    }
    return { values: pts, computedRange: r }
  }, [data, dateField, points, metric, range])

  if (values.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No date-keyed data for calendar view
      </div>
    )
  }

  const numericValues = values.map((p) => p[1])
  const min = Math.min(...numericValues)
  const max = Math.max(...numericValues)

  const option: any = {
    tooltip: {
      formatter: (params: any) => {
        const [date, v] = params.value
        return `<b>${date}</b><br/>${METRIC_LABELS[metric] ?? metric}: <b>${formatMetric(metric, v)}</b>`
      },
    },
    visualMap: {
      min,
      max,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      top: 0,
      textStyle: { fontSize: 10 },
    },
    calendar: {
      top: 50,
      left: 30,
      right: 30,
      cellSize: ['auto', 13],
      range: computedRange,
      itemStyle: { borderWidth: 0.5 },
      yearLabel: { show: typeof computedRange !== 'string' },
      dayLabel: { fontSize: 9 },
      monthLabel: { fontSize: 10 },
    },
    series: [
      {
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: values,
      },
    ],
  }

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onChartReady={makeChartReady(chartGroup)}
    />
  )
}
